import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useCan } from "@/lib/shell-context";
import { Pagination, pageOf } from "@/components/pagination";
import { api, type CreatedSource, type Source, type UnknownCode } from "@/lib/api";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  Mono,
  Panel,
  Select,
  Table,
  Td,
  Th,
} from "@/components/primitives";

/**
 * Sources and deep links (spec §12 Sources).
 *
 * Two counts per source, not one: first touch answers "who brought this
 * player", latest touch answers "who they came back through". They are
 * different questions and paying different people depends on which.
 */

export function SourcesPage() {
  const { botId } = useParams();
  const canManage = useCan("sources", "manage");
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [type, setType] = useState("influencer");
  const [copied, setCopied] = useState<string | null>(null);

  const sources = useQuery({
    queryKey: ["sources", botId],
    queryFn: () => api.get<Source[]>(`/bots/${botId}/sources`),
  });

  const unknown = useQuery({
    queryKey: ["unknown-codes", botId],
    queryFn: () => api.get<UnknownCode[]>(`/bots/${botId}/unknown-codes`),
  });

  const create = useMutation({
    mutationFn: () => api.post<CreatedSource>(`/bots/${botId}/sources`, { name, code, type }),
    onSuccess: () => {
      setName("");
      setCode("");
      void queryClient.invalidateQueries({ queryKey: ["sources", botId] });
      void queryClient.invalidateQueries({ queryKey: ["unknown-codes", botId] });
    },
  });

  // Browsing: search, type, order, and 10 a page by default (decision 44).
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [order, setOrder] = useState<"latest" | "first" | "name">("latest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [codesPage, setCodesPage] = useState(1);
  const [codesPageSize, setCodesPageSize] = useState(10);

  const needle = q.trim().toLowerCase();
  const filtered = (sources.data ?? [])
    .filter((s) => typeFilter === "all" || s.type === typeFilter)
    .filter(
      (s) =>
        !needle || s.name.toLowerCase().includes(needle) || s.code.toLowerCase().includes(needle),
    )
    .sort((a, b) =>
      order === "name"
        ? a.name.localeCompare(b.name)
        : order === "first"
          ? b.firstTouchPlayers - a.firstTouchPlayers
          : b.latestTouchPlayers - a.latestTouchPlayers,
    );
  const shown = pageOf(filtered, page, pageSize);
  const codes = pageOf(unknown.data ?? [], codesPage, codesPageSize);

  async function copy(link: string) {
    await navigator.clipboard.writeText(link);
    setCopied(link);
    setTimeout(() => setCopied(null), 1500);
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      {canManage && (
        <Panel title="Add a source">
          <form
            className="grid items-end gap-3 px-5 py-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_160px_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Name">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Influencer A"
                required
              />
            </Field>
            <Field label="Link code">
              <Input
                className="mono"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="inf_a"
                pattern="[A-Za-z0-9_-]{1,64}"
                required
              />
            </Field>
            <p className="-mt-1 text-muted-foreground sm:order-last sm:col-span-full">
              Letters, numbers, _ and - only. Cannot be changed later.
            </p>
            <Field label="Type">
              <Select className="w-full" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="influencer">Influencer</option>
                <option value="campaign">Campaign</option>
                <option value="other">Other</option>
              </Select>
            </Field>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Adding" : "Add source"}
            </Button>
          </form>
          {create.error && (
            <div className="px-5 pb-3">
              <ErrorNote error={create.error} />
            </div>
          )}
          {create.data && create.data.relinkedEvents > 0 && (
            <p className="border-t border-border px-5 py-2.5 text-ok">
              Credited {create.data.relinkedEvents} earlier{" "}
              {create.data.relinkedEvents === 1 ? "visit" : "visits"} that used this code before it
              existed.
            </p>
          )}
        </Panel>
      )}

      <Panel title="Sources">
        <div className="grid gap-2 border-b border-border px-5 py-3 sm:grid-cols-[minmax(0,1fr)_160px_200px]">
          <Input
            aria-label="Search sources"
            placeholder="Search by name or code"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
          <Select
            aria-label="Type"
            className="w-full"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All types</option>
            <option value="official">Official</option>
            <option value="influencer">Influencer</option>
            <option value="campaign">Campaign</option>
            <option value="other">Other</option>
          </Select>
          <Select
            aria-label="Order"
            className="w-full"
            value={order}
            onChange={(e) => setOrder(e.target.value as typeof order)}
          >
            <option value="latest">Sort: latest touch</option>
            <option value="first">Sort: first touch</option>
            <option value="name">Sort: name A–Z</option>
          </Select>
        </div>
        {sources.isLoading ? (
          <Loading />
        ) : sources.error ? (
          <div className="p-3.5">
            <ErrorNote error={sources.error} />
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th className="w-48">Deep link</Th>
                <Th className="w-28 text-right">First touch</Th>
                <Th className="w-28 text-right">Latest touch</Th>
              </tr>
            </thead>
            <tbody>
              {shown.rows.map((source) => (
                <tr key={source.id}>
                  <Td primary>
                    {source.name}
                    <span className="text-muted-foreground md:ml-2">{source.type}</span>
                  </Td>
                  <Td label="Deep link">
                    {source.deepLink ? (
                      <button
                        className="mono text-left text-[12px] break-all text-primary hover:underline"
                        onClick={() => void copy(source.deepLink!)}
                        title={source.deepLink}
                      >
                        {copied === source.deepLink ? "Copied" : `?start=${source.code}`}
                      </button>
                    ) : (
                      <Mono>{source.code}</Mono>
                    )}
                  </Td>
                  <Td label="First touch" className="text-right">
                    <Mono>{source.firstTouchPlayers}</Mono>
                  </Td>
                  <Td label="Latest touch" className="text-right">
                    <Mono>{source.latestTouchPlayers}</Mono>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {filtered.length === 0 && !sources.isLoading && (
          <Empty title="No source matches" hint="Try another name, code or type." />
        )}
        <Pagination
          page={shown.page}
          pageSize={pageSize}
          total={filtered.length}
          noun="sources"
          onPage={setPage}
          onPageSize={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </Panel>

      <Panel title="Codes with no source">
        {unknown.data && unknown.data.length > 0 ? (
          <>
            <p className="border-b border-border px-5 py-2.5 text-muted-foreground">
              Players arrived with these codes. Add a source using the same code and every past
              visit is credited to it.
            </p>
            <Table>
              <thead>
                <tr>
                  <Th>Code</Th>
                  <Th className="w-24 text-right">Uses</Th>
                  <Th className="w-32">Last used</Th>
                </tr>
              </thead>
              <tbody>
                {codes.rows.map((row) => (
                  <tr key={row.rawCode}>
                    <Td primary>
                      <button
                        className="mono text-left break-all text-primary hover:underline"
                        onClick={() => setCode(row.rawCode)}
                      >
                        {row.rawCode}
                      </button>
                    </Td>
                    <Td label="Uses" className="text-right">
                      <Mono>{row.uses}</Mono>
                    </Td>
                    <Td label="Last used" className="text-muted-foreground">
                      {new Date(row.lastSeenAt).toLocaleDateString()}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              page={codes.page}
              pageSize={codesPageSize}
              total={unknown.data.length}
              noun="codes"
              onPage={setCodesPage}
              onPageSize={(size) => {
                setCodesPageSize(size);
                setCodesPage(1);
              }}
            />
          </>
        ) : (
          <Empty
            title="Every code is accounted for"
            hint="Unrecognised deep links show up here so nothing goes uncredited."
          />
        )}
      </Panel>
    </div>
  );
}
